import { Menu, Transition } from "@headlessui/react";
import {
	ArrowDownTrayIcon,
	ChevronDownIcon,
	PencilSquareIcon,
	TrashIcon,
} from "@heroicons/react/20/solid";
import { ArrowPathRoundedSquareIcon, BookmarkIcon, ChevronDoubleDownIcon, ChevronDoubleUpIcon, StopCircleIcon } from '@heroicons/react/24/outline';
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../../../components/dialogs/confirm-dialog';
import { ChartStatusColorEnum, ChartStatusEnum } from '../../../../models/chart';
import { ClerkListResponse } from '../../../../models/clerks';

type AdminClerkDisplayProps = {
	clerk: ClerkListResponse;
	deleteClerk: (id: string) => void;
	editClerk: (id: string) => void;
	downloadCharts: (clerkId: string) => Promise<void>;
	printCharts: (clerkId: string) => Promise<void>;
	promoteClerk: (id: string) => void;
	removeClerkFromAdminRole: (id: string) => void;
	resetPassword: (id: string) => void;
	backgroundColor: string;
	markChartsAsPrinted: (clerkId: string) => Promise<void>;
};

const AdminClerkDisplay = (props: AdminClerkDisplayProps) => {
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] =
		useState<boolean>(false);
	const [makeAdminConfirmationOpen, setMakeAdminConfirmationOpen] =
		useState<boolean>(false);
	const [removeFromAdminConfirmationOpen, setRemoveFromAdminConfirmationOpen] =
		useState<boolean>(false);

	const {
		clerk,
		deleteClerk,
		downloadCharts,
		printCharts,
		promoteClerk,
		resetPassword,
		backgroundColor,
		markChartsAsPrinted,
	} = props;
	const navigate = useNavigate();

	return (
		<>
			<div
				className={`flex items-center w-full py-2 ${backgroundColor}`}
			>
				<div className="grid w-full grid-cols-8 p-3 ml-1 text-sm font-poppins justify-items-start">
					<p
						className='font-semibold underline uppercase cursor-pointer text-primary-blue'
						onClick={() => navigate(`/clerks/${clerk.id}`)}
					>
						view details
					</p>
					<p className={`${clerk.email.length > 20 ? "break-all pr-3 -ml-5" : '-ml-5'}`}>{clerk.email}</p>
					<p>{`${clerk.firstName} ${clerk.lastName}`}</p>
					<p>{clerk.county}</p>
					<p>{clerk.municipality}</p>
					<p>{clerk.state.name}</p>
					<p className={`${ChartStatusColorEnum[
						"Completed" as keyof typeof ChartStatusColorEnum
					]
						} p-1 rounded-sm uppercase text-xs h-[22px]`}>{clerk.completedCharts} charts completed</p>
					<Menu as="div" className="relative inline-block text-left">
						<div>
							<Menu.Button className="inline-flex justify-center w-full p-1 text-sm border rounded-md outline-none border-primary-blue text-primary-blue">
								Actions
								<ChevronDownIcon
									className="w-5 h-5 ml-2 -mr-1 outline-none text-primary-blue hover:text-primary-blue"
									aria-hidden="true"
								/>
							</Menu.Button>
						</div>
						<Transition
							as={Fragment}
							enter="transition ease-out duration-100"
							enterFrom="transform opacity-0 scale-95"
							enterTo="transform opacity-100 scale-100"
							leave="transition ease-in duration-75"
							leaveFrom="transform opacity-100 scale-100"
							leaveTo="transform opacity-0 scale-95"
						>
							<Menu.Items className="absolute right-0 z-10 w-48 p-2 mt-4 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none hover:cursor-pointer">
								<div className="text-xs">
									{clerk.completedCharts > 0 && (
										<>
											<Menu.Item>
												{({ active }) => (
													<div className="flex justify-center text-center">
														<ArrowDownTrayIcon
															className={`${active
																? "bg-primary-blue text-white border-1 border-primary-blue"
																: "text-gray"
																} h-10 w-10 p-2 rounded-l-md`}
															aria-hidden="true"
														/>
														<span
															onClick={() => printCharts(clerk.id)}
															className={`${active
																? "bg-primary-blue text-white"
																: "text-gray"
																} group flex rounded-r-md items-center w-full text-md font-semibold`}
														>
															Print Charts
														</span>
													</div>
												)}
											</Menu.Item>
											{clerk.charts.filter(chart => chart.printRequested && chart.status === ChartStatusEnum.Completed).length > 0 &&
												<Menu.Item>
													{({ active }) => (
														<div className="flex justify-center text-center">
															<BookmarkIcon
																className={`${active
																	? "bg-primary-blue text-white border-1 border-primary-blue"
																	: "text-gray"
																	} h-10 w-10 p-2 rounded-l-md`}
																aria-hidden="true"
															/>
															<span
																onClick={() => markChartsAsPrinted(clerk.id)}
																className={`${active
																	? "bg-primary-blue text-white"
																	: "text-gray"
																	} group flex rounded-r-md items-center w-full text-md font-semibold`}
															>
																Mark Charts as Printed
															</span>
														</div>
													)}
												</Menu.Item>
											}
											<Menu.Item>
												{({ active }) => (
													<div className="flex justify-center text-center">
														<ArrowDownTrayIcon
															className={`${active
																? "bg-primary-blue text-white border-1 border-primary-blue"
																: "text-gray"
																} h-10 w-10 p-2 rounded-l-md`}
															aria-hidden="true"
														/>
														<span
															onClick={() => downloadCharts(clerk.id)}
															className={`${active
																? "bg-primary-blue text-white"
																: "text-gray"
																} group flex rounded-r-md items-center w-full text-md font-semibold`}
														>
															Download Charts
														</span>
													</div>
												)}
											</Menu.Item>
										</>
									)}
									<Menu.Item>
										{({ active }) => (
											<div className="flex justify-center text-center">
												<PencilSquareIcon
													className={`${active
														? "bg-primary-blue text-white border-1 border-primary-blue"
														: "text-gray"
														} h-10 w-10 p-2 rounded-l-md`}
													aria-hidden="true"
												/>
												<span
													onClick={() => {
														navigate(`edit/${clerk.id}`);
													}}
													className={`${active
														? "bg-primary-blue text-white"
														: "text-gray"
														} group flex rounded-r-md items-center w-full text-md font-semibold`}
												>
													Edit
												</span>
											</div>
										)}
									</Menu.Item>
									<Menu.Item>
										{({ active }) => (
											<div className="flex justify-center text-center">
												<TrashIcon
													className={`${active
														? "bg-primary-blue text-white border-1 border-primary-blue"
														: "text-gray"
														} h-10 w-10 p-2 rounded-l-md`}
													aria-hidden="true"
												/>
												<span
													onClick={() => setDeleteConfirmationOpen(true)}
													className={`${active
														? "bg-primary-blue text-white"
														: "text-gray"
														} group flex rounded-r-md items-center w-full text-md font-semibold`}
												>
													Delete
												</span>
											</div>
										)}
									</Menu.Item>
									{clerk!.isAdmin && clerk!.isDisabled && (
										<Menu.Item>
											{({ active }) => (
												<div className="flex justify-center text-center">
													<ChevronDoubleUpIcon
														className={`${active
															? "bg-primary-blue text-white border-1 border-primary-blue"
															: "text-gray"
															} h-10 w-10 p-2 rounded-l-md`}
														aria-hidden="true"
													/>
													<span
														onClick={() => setMakeAdminConfirmationOpen(true)}
														className={`${active
															? "bg-primary-blue text-white"
															: "text-gray"
															} group flex rounded-r-md items-center w-full text-md font-semibold`}
													>
														Make Admin
													</span>
												</div>
											)}
										</Menu.Item>
									)}
									{clerk.isAdmin && !clerk.isDisabled && (
										<Menu.Item>
											{({ active }) => (
												<div className="flex justify-center text-center">
													<StopCircleIcon
														className={`${active
															? "bg-primary-blue text-white border-1 border-primary-blue"
															: "text-gray"
															} h-10 w-10 p-2 rounded-l-md`}
														aria-hidden="true"
													/>
													<span
														onClick={() => setRemoveFromAdminConfirmationOpen(true)}
														className={`${active
															? "bg-primary-blue text-white"
															: "text-gray"
															} group flex rounded-r-md items-center w-full text-md font-semibold`}
													>
														Remove Admin
													</span>
												</div>
											)}
										</Menu.Item>
									)}
									<Menu.Item>
										{({ active }) => (
											<div className="flex justify-center text-center">
												<ArrowPathRoundedSquareIcon
													className={`${active
														? "bg-primary-blue text-white border-1 border-primary-blue"
														: "text-gray"
														} h-10 w-10 p-2 rounded-l-md`}
													aria-hidden="true"
												/>
												<span
													onClick={() => resetPassword(clerk.id)}
													className={`${active
														? "bg-primary-blue text-white"
														: "text-gray"
														} group flex rounded-r-md items-center w-full text-md font-semibold`}
												>
													Force Password Reset
												</span>
											</div>
										)}
									</Menu.Item>
								</div>
							</Menu.Items>
						</Transition>
					</Menu>
				</div>
			</div>
			<ConfirmDialog
				isOpen={deleteConfirmationOpen}
				toggle={() => setDeleteConfirmationOpen(!deleteConfirmationOpen)}
				onSave={() => {
					deleteClerk(clerk.id);
					setDeleteConfirmationOpen(!deleteConfirmationOpen);
				}}
				title="Delete Clerk"
				description="Are you sure you want to delete this clerk?"
				requireTextConfirmation={true}
				textConfirmationValue='delete'
				acceptText='Delete'
				rejectText='Cancel'
			/>
			<ConfirmDialog
				isOpen={makeAdminConfirmationOpen}
				toggle={() => setMakeAdminConfirmationOpen(!makeAdminConfirmationOpen)}
				onSave={() => {
					promoteClerk(clerk.id);
					setMakeAdminConfirmationOpen(!makeAdminConfirmationOpen);
				}}
				title="Make Admin"
				description="Are you sure you want to make this clerk an admin?"
				requireTextConfirmation={true}
				textConfirmationValue='admin'
				acceptText='Make Admin'
				rejectText='Cancel'
			/>
			<ConfirmDialog
				isOpen={removeFromAdminConfirmationOpen}
				toggle={() => setRemoveFromAdminConfirmationOpen(!removeFromAdminConfirmationOpen)}
				onSave={() => {
					promoteClerk(clerk.id);
					setRemoveFromAdminConfirmationOpen(!removeFromAdminConfirmationOpen);
				}}
				title="Revoke Admin Privileges"
				description="Are you sure you want to make this clerk non-admin?"
				requireTextConfirmation={true}
				textConfirmationValue='non-admin'
				acceptText='Make Non-Admin'
				rejectText='Cancel'
			/>
		</>
	)
};

export default AdminClerkDisplay;
